import { badgeVariants } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useRoleLabel } from "@/hooks/useRoleLabel";
import { cn } from "@/lib/utils";
import { ChevronDown, Loader2 } from "lucide-react";

const PILL_CLASS = cn(badgeVariants({ variant: "outline" }), "gap-1 font-normal");

export interface RolePillProps {
  role: string;
  /** Roles the actor may switch to (includes the current one); empty = read-only. */
  assignableRoles: string[];
  saving?: boolean;
  onChange?: (role: string) => void;
}

/**
 * An org member's role as a pill. Every row renders the same pill; the ones the
 * actor may change add a chevron and open a menu of the assignable roles.
 */
export function RolePill({ role, assignableRoles, saving = false, onChange }: RolePillProps) {
  const { t } = useLanguage();
  const roleLabel = useRoleLabel();
  const descriptions = t.settings.roles.roleDescriptions as Record<string, string>;

  if (assignableRoles.length === 0 || !onChange) {
    return <span className={PILL_CLASS}>{roleLabel(role)}</span>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={saving}
        aria-label={`${t.settings.roles.role}: ${roleLabel(role)}`}
        className={cn(
          PILL_CLASS,
          "cursor-pointer hover:bg-accent disabled:cursor-default disabled:opacity-60 data-[state=open]:bg-accent"
        )}
      >
        {roleLabel(role)}
        {saving ? (
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        ) : (
          <ChevronDown className="h-3 w-3 text-muted-foreground" aria-hidden />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuRadioGroup
          value={role}
          onValueChange={(next) => {
            if (next !== role) onChange(next);
          }}
        >
          {assignableRoles.map((option) => (
            <DropdownMenuRadioItem key={option} value={option} className="items-start">
              <div className="flex flex-col gap-0.5">
                <span>{roleLabel(option)}</span>
                {descriptions[option] && (
                  <span className="text-xs text-muted-foreground">{descriptions[option]}</span>
                )}
              </div>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
